import { usePrivy } from "@privy-io/react-auth";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, Route, useLocation } from "react-router-dom";
import config from "@/config";
import { useUserActivity } from "@/features/auth/hooks/use-auth";
import { jwtPayload, role } from "@/features/auth/types";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { Routes } from "@/features/router";
import { useAppSelector } from "@/redux/hooks";
import {
  getExpirationDate,
  getUserRoles,
  setExpirationToken,
  setLinkedCompanies,
  setRoles
} from "@/redux/slices/userSlice";
import { Layout, LayoutTitle } from "./_layout/index.react";
import { SecurityApp } from "./account/security/index.react";
import Admin from "./admin/AdminOlkypass";
import { Company } from "./company";
import { CompanyRelatedPerson } from "./company/components/CompanyRelatedPerson";
import { Transactions } from "./dashboard/components/transactions";
import { DashboardApp } from "./dashboard/index.react";
import { AliasesAddressApp } from "./identity/components/aliases-address/index.react";
import { AliasesDocumentsApp } from "./identity/components/aliases-documents/index.react";
import { AliasesEmailApp } from "./identity/components/aliases-email/index.react";
import { AliasMobilePage } from "./identity/components/aliases-mobile/index.react";
import { AliasesPaymentApp } from "./identity/components/aliases-payment/index.react";
import { AliasesPublicApp } from "./identity/components/aliases-public/index.react";
import { Level } from "./identity/components/level/index.react";
import { SupportApp } from "./support/index.react";
import { useDispatch } from "react-redux";
import { getJwt } from "@/features/auth/utils";
import { jwtDecode } from "@/features/auth/invalid-token-error";
import { AdminNew } from "./admin/NewAdminOlkypass";
import { PrivateForceWebauthnRoutes } from "@/privateRoutes/PrivateForceWebauthnRoutes";
import { PrivateCompanyRoutes } from "@/privateRoutes/PrivateCompanyRoutes";
import { CompanyOnboarding } from "./company-onboarding/company-onboarding";
import { PrivateCompanyPaidRoutes } from "@/privateRoutes/PrivateCompanyPaidRoutes";

export const PassRoot = () => {
  const origin = useOrigin();
  const dispatch = useDispatch();
  const userRoles = useAppSelector(getUserRoles);
  const token = getJwt().token;
  const userExpDate = useAppSelector(getExpirationDate);

  useUserActivity();

  const { logout, authenticated } = usePrivy();

  const { leUuid } = useCompanyContext();
  const companyId = sessionStorage.getItem("legalUUid");
  useEffect(() => {
    if (!leUuid && companyId) {
      if (authenticated) {
        logout();
      }
      sessionStorage.removeItem("legalUUid");
    } else if (leUuid && leUuid !== companyId) {
      if (authenticated) {
        logout();
      }
      sessionStorage.setItem("legalUUid", leUuid);
    } else if (leUuid && !companyId) {
      if (authenticated) {
        logout();
      }
      sessionStorage.setItem("legalUUid", leUuid);
    }
  }, [companyId, leUuid]);
  useEffect(() => {
    if (token) {
      const { linked_companies, roles, exp }: jwtPayload = jwtDecode(token);
      if (userRoles.length === 0) {
        dispatch(setRoles({ roles: roles }));
      }
      dispatch(setLinkedCompanies({ linkedCompanies: linked_companies }));
      if (Number(exp) !== userExpDate) dispatch(setExpirationToken(Number(exp)));
    }
  }, [token]);
  return (
    <Route element={<Layout />}>
      {userRoles.includes(role.ADMIN) ? (
        <Route>
          <Route path={Routes.dashboard} element={<Admin />} />
          <Route path={`${Routes.pass}/new`} element={<AdminNew />} />
          <Route path={Routes.company_related_person} element={<CompanyRelatedPerson />} />
          <Route path={Routes.level_company} element={<Level />} />
        </Route>
      ) : (
        <Route
          path=""
          element={
            <>
              <Outlet />
            </>
          }
        >
          {origin === FromURL.None ? (
            <Route path="" element={<WithVueTitle />}>
              <Route path="*" element={<Navigate to={Routes.dashboard} />} />
              <Route path="" element={<Navigate to={Routes.dashboard} />} />
            </Route>
          ) : (
            <Route path="" element={<WithVueTitle />}>
              <Route element={<PrivateForceWebauthnRoutes />}>
                <Route
                  path="*"
                  element={
                    <Navigate to={`${Routes.dashboard}/${sessionStorage.getItem("origin")}`} />
                  }
                />
                <Route
                  path=""
                  element={
                    <Navigate to={`${Routes.dashboard}/${sessionStorage.getItem("origin")}`} />
                  }
                />
              </Route>
            </Route>
          )}
          <Route path={Routes.security} element={<SecurityApp />} />
          <Route path={Routes.support} element={<SupportApp />} />
          <Route
            path={origin === FromURL.None ? Routes.dashboard : Routes.dashboard_partner}
            element={<DashboardApp origin={origin} />}
          />
          <Route element={<PrivateForceWebauthnRoutes />}>
            <Route path={Routes.account_alias_email} element={<AliasesEmailApp />} />
            <Route path={Routes.level} element={<Level />} />

            {config.ACTIVATE_OLKYPASS_PRO && (
              <Route path={Routes.companies} element={<Company />} />
            )}
            <Route path={Routes.account_alias_documents} element={<AliasesDocumentsApp />} />

            <Route element={<PrivateCompanyRoutes />}>
              <Route path={Routes.company_onboarding} element={<CompanyOnboarding />} />

              <Route element={<PrivateCompanyPaidRoutes />}>
                <Route
                  path={
                    origin === FromURL.None
                      ? Routes.dashboard_company
                      : Routes.dashboard_company_partner
                  }
                  element={<DashboardApp origin={origin} />}
                />
                <Route path={Routes.level_company} element={<Level />} />
                <Route path={Routes.account_alias_email_company} element={<AliasesEmailApp />} />

                <Route
                  path={Routes.account_alias_address_company}
                  element={<AliasesAddressApp />}
                />
                <Route
                  path={Routes.account_alias_documents_company}
                  element={<AliasesDocumentsApp />}
                />
                <Route path={Routes.account_alias_documents} element={<AliasesDocumentsApp />} />
                <Route path={Routes.account_alias_public_company} element={<AliasesPublicApp />} />
                <Route
                  path={Routes.account_alias_payments_company}
                  element={<AliasesPaymentApp />}
                />
                <Route path={Routes.account_transactions_company} element={<Transactions />} />
                <Route path={Routes.account_alias_mobile_company} element={<AliasMobilePage />} />
                <Route path={Routes.company_related_person} element={<CompanyRelatedPerson />} />
              </Route>
            </Route>

            <Route path={Routes.account_alias_address} element={<AliasesAddressApp />} />
            <Route path={Routes.account_alias_public} element={<AliasesPublicApp />} />
            <Route path={Routes.account_alias_payments} element={<AliasesPaymentApp />} />
            <Route path={Routes.account_transactions} element={<Transactions />} />
            <Route path={Routes.account_alias_mobile} element={<AliasMobilePage />} />
          </Route>
        </Route>
      )}
      <Route path="*" element={<Navigate to={Routes.dashboard} />} />
      <Route path="" element={<Navigate to={Routes.dashboard} />} />
    </Route>
  );
};

/** This is temporary until we replace vue component by react ones */
export const WithVueTitle = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  let found = false;
  return (
    <>
      {Routes.security.indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("olkypass.mainPage.navigation_menu.link.security")}</LayoutTitle>
      )}
      {(Routes.level + "/old").indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("olkypass.mainPage.navigation_menu.link.my_pass")}</LayoutTitle>
      )}
      {/* {Routes.companies.indexOf(pathname) === 0 && !found && (found = true) && <LayoutTitle>{t('olkypass.mainPage.navigation_menu.link.companies')}</LayoutTitle>}*/}

      {Routes.support.indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("olkypass.mainPage.navigation_menu.link.support")}</LayoutTitle>
      )}
      {Routes.kycoin.indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("olkypass.mainPage.navigation_menu.link.olkyKycoin")}</LayoutTitle>
      )}
      {/*     {Routes.companies.indexOf(pathname) === 0 && !found && (found = true) && <LayoutTitle>{t('olkypass.mainPage.navigation_menu.link.companies')}</LayoutTitle>}*/}
      {Routes.dashboard.indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("olkypass.mainPage.navigation_menu.link.home")}</LayoutTitle>
      )}
      {Routes.account_alias_email.indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("aliasEmailPage.title")}</LayoutTitle>
      )}
      {Routes.level.indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("olkypass.mainPage.navigation_account_menu.link.level")}</LayoutTitle>
      )}
      {Routes.account_alias_mobile.indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("aliasMobilePage.title")}</LayoutTitle>
      )}
      {Routes.account_alias_address.indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("aliasAddressPage.title")}</LayoutTitle>
      )}
      {Routes.account_alias_documents.indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("aliasDocumentsPage.title")}</LayoutTitle>
      )}
      {Routes.account_alias_payments.indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("aliasPaymentsPage.title")}</LayoutTitle>
      )}
      {Routes.account_alias_public.indexOf(pathname) === 0 && !found && (found = true) && (
        <LayoutTitle>{t("aliasPublicPage.title")}</LayoutTitle>
      )}
      <Outlet />
    </>
  );
};
